// src/hooks/useGeolocationWatch.js

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { hasGeolocation, isBrowser } from "../utils/platform";

/**
 * Watches user's position with navigator.geolocation.watchPosition.
 * Safe defaults, includes throttling to avoid excessive renders.
 *
 * Returns:
 * - position: { latitude, longitude, accuracy, altitude, altitudeAccuracy, heading, speed, timestamp } | null
 * - status: "idle" | "watching" | "error" | "unsupported"
 * - error: { code, message } | null
 * - start: () => void
 * - stop: () => void
 */
export function useGeolocationWatch(options) {
  const {
    enabled = true,
    highAccuracy = true,
    timeoutMs = 15000,
    maximumAgeMs = 1000,
    throttleMs = 250,
  } = options || {};

  const [status, setStatus] = useState(() => {
    if (!isBrowser()) return "unsupported";
    return hasGeolocation() ? "idle" : "unsupported";
  });

  const [error, setError] = useState(null);
  const [position, setPosition] = useState(null);

  const watchIdRef = useRef(null);
  const lastUpdateAtRef = useRef(0);
  const enabledRef = useRef(Boolean(enabled));
  enabledRef.current = Boolean(enabled);

  const stop = useCallback(() => {
    if (!isBrowser() || !hasGeolocation()) return;
    const id = watchIdRef.current;
    if (typeof id === "number") {
      try {
        navigator.geolocation.clearWatch(id);
      } catch {
        // ignore
      }
    }
    watchIdRef.current = null;
    lastUpdateAtRef.current = 0;
    setStatus((prev) => (prev === "unsupported" ? prev : "idle"));
  }, []);

  const start = useCallback(() => {
    if (!isBrowser() || !hasGeolocation()) {
      setStatus("unsupported");
      return;
    }

    // prevent duplicate watches
    if (typeof watchIdRef.current === "number") return;

    setError(null);
    setStatus("watching");

    const geoOptions = {
      enableHighAccuracy: Boolean(highAccuracy),
      timeout: Math.max(0, Number(timeoutMs) || 0),
      maximumAge: Math.max(0, Number(maximumAgeMs) || 0),
    };

    const onSuccess = (pos) => {
      const now = Date.now();
      const last = lastUpdateAtRef.current;
      const t = Math.max(0, Number(throttleMs) || 0);
      if (t > 0 && now - last < t) return;

      lastUpdateAtRef.current = now;

      const coords = pos && pos.coords ? pos.coords : null;
      const lat = coords && typeof coords.latitude === "number" ? coords.latitude : null;
      const lng = coords && typeof coords.longitude === "number" ? coords.longitude : null;

      if (lat === null || lng === null || !Number.isFinite(lat) || !Number.isFinite(lng)) {
        return;
      }

      setPosition({
        latitude: lat,
        longitude: lng,
        accuracy: typeof coords.accuracy === "number" ? coords.accuracy : null,
        altitude: typeof coords.altitude === "number" ? coords.altitude : null,
        altitudeAccuracy:
          typeof coords.altitudeAccuracy === "number" ? coords.altitudeAccuracy : null,
        heading: typeof coords.heading === "number" ? coords.heading : null,
        speed: typeof coords.speed === "number" ? coords.speed : null,
        timestamp: typeof pos.timestamp === "number" ? pos.timestamp : Date.now(),
      });
    };

    const onError = (err) => {
      const code = err && typeof err.code === "number" ? err.code : 0;
      const message = err && typeof err.message === "string" ? err.message : "Geolocation error";
      setError({ code, message });
      setStatus("error");
    };

    try {
      const id = navigator.geolocation.watchPosition(onSuccess, onError, geoOptions);
      watchIdRef.current = id;
    } catch (e) {
      setError({ code: 0, message: e instanceof Error ? e.message : "Geolocation error" });
      setStatus("error");
    }
  }, [highAccuracy, maximumAgeMs, throttleMs, timeoutMs]);

  // Auto start/stop when enabled changes
  useEffect(() => {
    if (!isBrowser() || !hasGeolocation()) {
      setStatus("unsupported");
      return undefined;
    }

    if (enabled) start();
    else stop();

    return () => {
      stop();
    };
  }, [enabled, start, stop]);

  const api = useMemo(
    () => ({
      position,
      status,
      error,
      start,
      stop,
    }),
    [position, status, error, start, stop]
  );

  return api;
}
